import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { queryKeys } from '@/api/query-keys';
import { Badge, Icon, Input, PhasePill } from '@/ui';
import { useUiPrefs } from '@/store/ui-prefs';
import { isNavEnabled } from './nav';
import { AGENT_ENABLED } from '@/config/features';
import type {
  ComponentHit,
  IdeaHit,
  InitiativeHit,
  ProjectHit,
  SearchResults,
  TicketHit,
} from '@/types/api';

const QUESTION_RE = /\?\s*$|^\s*(who|what|when|where|why|how|which|should|can|is|are|do|does|will|tell\s+me|explain|compare|summari[sz]e)\b/i;

const PRIORITY_TONE: Record<string, 'risk' | 'warn' | 'neutral'> = {
  Critical: 'risk',
  High: 'warn',
  Normal: 'neutral',
  Low: 'neutral',
};

/**
 * Topbar search. Behaves two ways:
 *   • Search terms → debounced fetch → dropdown of grouped results.
 *   • Question (ends with ? or opens with a question word) → opens the
 *     left-side search chat panel with the input as the seed prompt.
 *
 * Enter routes by intent: questions go to chat, terms navigate to the
 * highest-priority first result if any.
 */
export function SearchBar() {
  const [value, setValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const openChat = useUiPrefs((s) => s.openSearchChat);

  // With the agent parked, the search box is search-only — never route to chat.
  const isQuestion = useMemo(() => AGENT_ENABLED && QUESTION_RE.test(value.trim()), [value]);

  // Debounce typed input by 220ms before firing the network call.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value.trim()), 220);
    return () => window.clearTimeout(t);
  }, [value]);

  // Don't bother querying when input looks like a question — we'll route
  // that to chat rather than show results.
  const shouldQuery = debounced.length >= 2 && !isQuestion;

  const resultsQ = useQuery({
    queryKey: queryKeys.search.query(debounced),
    queryFn: () => api.search.query(debounced),
    enabled: shouldQuery,
    staleTime: 1000 * 30,
  });

  // Flatten results into a single ordered list so arrow-key navigation
  // and Enter-on-first-result are easy.
  const flat = useMemo(
    () => flattenResults(resultsQ.data ?? null),
    [resultsQ.data],
  );

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Reset highlight whenever results refresh.
  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (e.key === 'Enter') {
      if (isQuestion && value.trim()) {
        openChat(value.trim());
        setOpen(false);
        return;
      }
      const hit = flat[activeIndex];
      if (hit) {
        navigate(hit.href);
        setValue('');
        setOpen(false);
      }
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
  };

  const totalHits =
    (isNavEnabled('projects') ? resultsQ.data?.projects.total ?? 0 : 0) +
    (isNavEnabled('initiatives') ? resultsQ.data?.initiatives.total ?? 0 : 0) +
    (isNavEnabled('ideas') ? resultsQ.data?.ideas.total ?? 0 : 0) +
    (isNavEnabled('tickets') ? resultsQ.data?.tickets.total ?? 0 : 0) +
    (isNavEnabled('components') ? resultsQ.data?.components.total ?? 0 : 0);

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: 420 }}>
      <Input
        placeholder={AGENT_ENABLED ? 'Search or ask a question…' : 'Search…'}
        value={value}
        onChange={setValue}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        prefix={<Icon name={isQuestion ? 'chat' : 'search'} size={14} />}
      />
      {open && value.trim().length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--apm-card)',
            border: '1px solid var(--apm-line)',
            borderRadius: 'var(--apm-radius-lg)',
            boxShadow: 'var(--apm-shadow-lg)',
            maxHeight: 480,
            overflowY: 'auto',
            zIndex: 30,
          }}
        >
          {isQuestion ? (
            <button
              onClick={() => {
                openChat(value.trim());
                setOpen(false);
              }}
              style={{
                all: 'unset',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 'var(--apm-card-pad)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Icon name="chat" size={16} style={{ color: 'var(--apm-accent)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--apm-ink)' }}>
                  Ask Atlas
                </div>
                <div style={{ fontSize: 12, color: 'var(--apm-muted)', marginTop: 2 }}>
                  Press Enter to open the chat panel with “{truncate(value, 80)}”
                </div>
              </div>
              <Icon name="arrow_right" size={12} style={{ color: 'var(--apm-muted)' }} />
            </button>
          ) : resultsQ.isLoading ? (
            <div style={{ padding: 'var(--apm-card-pad)', color: 'var(--apm-muted)', fontSize: 13 }}>
              Searching…
            </div>
          ) : !resultsQ.data || totalHits === 0 ? (
            <div style={{ padding: 'var(--apm-card-pad)', color: 'var(--apm-muted)', fontSize: 13 }}>
              No matches for “{truncate(debounced, 60)}”. Try a question instead — start with how,
              what, who, why…
            </div>
          ) : (
            <Groups
              data={resultsQ.data}
              flat={flat}
              activeIndex={activeIndex}
              onHover={setActiveIndex}
              onPick={(href) => {
                navigate(href);
                setValue('');
                setOpen(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface FlatHit {
  key: string;
  group: 'projects' | 'initiatives' | 'ideas' | 'tickets' | 'components';
  href: string;
  primary: string;
  secondary?: string;
  badge?: React.ReactNode;
  trailing?: React.ReactNode;
}

// Focus mode: Initiatives, Tickets, and Components are disabled elsewhere in
// the app, so search results for them are dropped here too — otherwise the
// search box would be the one remaining way to route to a disabled page.
function flattenResults(r: SearchResults | null): FlatHit[] {
  if (!r) return [];
  const out: FlatHit[] = [];
  if (isNavEnabled('projects')) for (const p of r.projects.results) out.push(projectHit(p));
  if (isNavEnabled('initiatives')) for (const i of r.initiatives.results) out.push(initiativeHit(i));
  if (isNavEnabled('ideas')) for (const i of r.ideas.results) out.push(ideaHit(i));
  if (isNavEnabled('tickets')) for (const t of r.tickets.results) out.push(ticketHit(t));
  if (isNavEnabled('components')) for (const c of r.components.results) out.push(componentHit(c));
  return out;
}

function projectHit(p: ProjectHit): FlatHit {
  return {
    key: `project-${p.id}`,
    group: 'projects',
    href: `/projects/${p.id}`,
    primary: p.name,
    secondary: p.owner_name ?? undefined,
    badge: <PhasePill phase={p.status} />,
    trailing: p.involves_phi_pii ? <Badge tone="risk" size="sm">PHI</Badge> : null,
  };
}
function initiativeHit(i: InitiativeHit): FlatHit {
  return {
    key: `initiative-${i.id}`,
    group: 'initiatives',
    href: `/initiatives/${i.id}`,
    primary: i.name,
    secondary: i.project_name ?? `Project #${i.project_id}`,
    badge: <Badge tone="info" size="sm">{i.status}</Badge>,
  };
}
function ideaHit(i: IdeaHit): FlatHit {
  return {
    key: `idea-${i.id}`,
    group: 'ideas',
    href: `/ideas`,
    primary: i.title,
    secondary: i.submitter_name ? `by ${i.submitter_name}` : undefined,
    badge: <Badge tone="info" size="sm">{i.status}</Badge>,
  };
}
function ticketHit(t: TicketHit): FlatHit {
  return {
    key: `ticket-${t.id}`,
    group: 'tickets',
    href: `/tickets?open=${t.id}`,
    primary: t.title,
    secondary: t.project_name ?? `Project #${t.project_id}`,
    badge: <Badge tone={PRIORITY_TONE[t.priority] ?? 'neutral'} size="sm" dot>{t.priority}</Badge>,
    trailing: <Badge tone="outline" size="sm">{t.status}</Badge>,
  };
}
function componentHit(c: ComponentHit): FlatHit {
  return {
    key: `component-${c.id}`,
    group: 'components',
    href: `/components`,
    primary: c.name,
    secondary: c.type,
    badge: <Badge tone={c.phi_safe ? 'ok' : 'neutral'} size="sm">{c.status}</Badge>,
  };
}

function Groups({
  data,
  flat,
  activeIndex,
  onHover,
  onPick,
}: {
  data: SearchResults;
  flat: FlatHit[];
  activeIndex: number;
  onHover: (i: number) => void;
  onPick: (href: string) => void;
}) {
  let cursor = 0;
  const allGroups: { id: FlatHit['group']; label: string; total: number; count: number }[] = [
    { id: 'projects', label: 'Projects', total: data.projects.total, count: data.projects.results.length },
    { id: 'initiatives', label: 'Initiatives', total: data.initiatives.total, count: data.initiatives.results.length },
    { id: 'ideas', label: 'Ideas', total: data.ideas.total, count: data.ideas.results.length },
    { id: 'tickets', label: 'Tickets', total: data.tickets.total, count: data.tickets.results.length },
    { id: 'components', label: 'Components', total: data.components.total, count: data.components.results.length },
  ];
  const groups = allGroups.filter((g) => isNavEnabled(g.id));

  return (
    <div>
      {groups.map((g) => {
        if (g.count === 0) return null;
        const start = cursor;
        const end = cursor + g.count;
        cursor = end;
        const slice = flat.slice(start, end);
        return (
          <div key={g.id} style={{ borderBottom: '1px solid var(--apm-line)' }}>
            <div
              style={{
                padding: '8px var(--apm-card-pad)',
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: 'var(--apm-muted)',
                fontWeight: 500,
                display: 'flex',
                justifyContent: 'space-between',
                background: 'var(--apm-bg-2)',
              }}
            >
              <span>{g.label}</span>
              <span style={{ color: 'var(--apm-muted-2)' }}>
                {g.count}{g.total > g.count ? ` of ${g.total}` : ''}
              </span>
            </div>
            {slice.map((hit, idxInSlice) => {
              const absoluteIdx = start + idxInSlice;
              const active = absoluteIdx === activeIndex;
              return (
                <button
                  key={hit.key}
                  onMouseEnter={() => onHover(absoluteIdx)}
                  onClick={() => onPick(hit.href)}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--apm-inner-gap)',
                    padding: 'var(--apm-row-pad) var(--apm-card-pad)',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: active ? 'var(--apm-bg-sunken)' : 'transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: 'var(--apm-ink)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {hit.primary}
                    </div>
                    {hit.secondary && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--apm-muted)',
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {hit.secondary}
                      </div>
                    )}
                  </div>
                  {hit.badge}
                  {hit.trailing}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
